import React, { useRef, useState, useCallback, memo } from "react";
import { IColors, ITypography } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { TooltipV2 } from "@cart-ui/widgets/Tooltip/v2";
import { DisplayTextWrapper, TitleWrapper } from "./TooltipContainerV2.style";

interface TooltipContainerProps {
    displayText: string;
    className?: string;
    toolTipMeta: {
        title: string;
        description: string;
    };
    onClickTooltipEvent?: VoidFunction;
    displayTextColor?: keyof IColors;
    displayTextVariant?: keyof ITypography;
    titleTextColor?: keyof IColors;
    titleTextVariant?: keyof ITypography;
    descriptionTextColor?: keyof IColors;
    descriptionTextVariant?: keyof ITypography;
}

const TooltipContainerV2 = (props: TooltipContainerProps) => {
    const { displayText, toolTipMeta, onClickTooltipEvent } = props;
    const { title, description } = toolTipMeta;
    const triggerRef = useRef<HTMLDivElement>(null);
    const [showInfo, setShowInfo] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const handleShowInfo = useCallback(
        event => {
            event.stopPropagation();
            if (!showInfo && event.target) {
                setShowInfo(true);
                onClickTooltipEvent?.();
                const eventTarget = event.target as HTMLElement;
                const { x, y, left, top, width } = eventTarget.getBoundingClientRect();
                // not all browsers gives x, y so we use left and top values in such cases
                setPosition({ x: (x || left) + width / 2, y: y || top });
            }
        },
        [onClickTooltipEvent, showInfo],
    );

    const handleHideInfo = useCallback(() => {
        setShowInfo(false);
    }, []);

    return (
        <>
            <div ref={triggerRef} onClick={handleShowInfo}>
                <DisplayTextWrapper
                    variant={props.displayTextVariant ?? "V2.body2.neutral.medium"}
                    color={props.displayTextColor ?? "text_High_Emphasis"}
                >
                    {displayText}
                </DisplayTextWrapper>
            </div>
            {showInfo && (
                <TooltipV2 position={position} hide={handleHideInfo} widgetSize={"230"} triggerRef={triggerRef}>
                    <TitleWrapper
                        variant={props.titleTextVariant ?? "V2.body1.neutral.bold"}
                        color={props.titleTextColor ?? "text_Highest_Emphasis"}
                    >
                        {title}
                    </TitleWrapper>
                    <Text
                        variant={props.descriptionTextVariant ?? "V2.body2.neutral.regular"}
                        color={props.descriptionTextColor ?? "text_High_Emphasis"}
                    >
                        {description}
                    </Text>
                </TooltipV2>
            )}
        </>
    );
};

export default memo(TooltipContainerV2);
